import { PhotographIcon } from "@heroicons/react/outline";
import { useCreate } from "@refinedev/core";
import { Loader } from "components/loader";
import { isEmpty } from "lodash";
import React from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { bytesForHuman, isInvalidImageSize } from "utills/helpers";

interface DropzoneProps {
  id?: string;
  value?: string[];
  [x: string]: any;
}
const FilesDropzone = React.forwardRef(
  ({ onChange }: DropzoneProps, ref: any) => {
    const { mutate, isLoading: isLoading } = useCreate();

    const _uploadFiles = async (file: File) => {
      const formData = new FormData();
      formData.append("upload", file, `${file.name}`);
      return new Promise((resolve, reject) => {
        mutate(
          {
            dataProviderName: "externalUpload",
            resource: "/",
            values: formData,
          },
          {
            onSuccess: (data) => {
              const imageUrl = data.data?.result.img_url;
              resolve(imageUrl || "");
            },
          }
        );
      });
    };

    const {
      getRootProps: getRootkProps,
      getInputProps,
      open: openUpload,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: {
        "image/jpeg": [".jpeg", ".png", ".jpg"],
        "application/pdf": ["pdf"],
      },
      validator: (file: File) => {
        const maximumSize = 3145728; // 3 MB
        if (isInvalidImageSize(file, maximumSize)) {
          return {
            code: "invalid-size",
            message: `Invalid file size.\n Maximum size: ${bytesForHuman(
              maximumSize
            )}.`,
          };
        }
        return null;
      },
      onDrop: async (acceptedFiles, rejectedFiles) => {
        console.log(acceptedFiles);
        if (acceptedFiles.length > 0 && isEmpty(rejectedFiles)) {
          for (let i = 0; i <acceptedFiles.length; i++) {
            const fileUrl: string = (await _uploadFiles(
              acceptedFiles[i]
            )) as string;
            if (!fileUrl) return;
            onChange(fileUrl);
          }
        }
      },
      onDropRejected: (errorFiles) => {
        if (!isEmpty(errorFiles)) {
          if (errorFiles[0].errors[0].code === "file-invalid-type") {
            return toast.error(
              `Only PNG, JPG, JPEG and PDF file formats are supported.`
            );
          }
          return toast.error(errorFiles[0].errors[0].message);
        }
      },
    });

    return (
      <section className="w-full h-full">
        <div {...getRootkProps({ className: "h-full" })}>
          <div
            className="
            relative overflow-hidden
            flex flex-col justify-center items-center 
            rounded-lg 
            border border-dashed border-gray-900/25 bg-gray-50
            px-6 py-8 
            h-auto 
            text-center
          "
          >
            {isLoading ? (
              <div
                className="
                  opacity-100
                bg-gray-900/40
                  duration-300
                  absolute
                  inset-0
                  flex
                  justify-center
                  items-center
                  rounded-lg
                "
              >
                <Loader size={40} />
              </div>
            ) : null}

            <div>
              <PhotographIcon
                className="h-12 w-12 text-gray-300 mx-auto"
                aria-hidden="true"
              />
              <input {...getInputProps()} />
              <div className="mt-4">
                <p>Drag and drop file or</p>
                <button
                  type="button"
                  onClick={openUpload}
                  className="font-semibold text-main-blue-600 focus-within:outline-none focus-within:ring-2 hover:text-main-bllue-500 focus:ring-transparent"
                >
                  browse file
                </button>
              </div>
            </div>
          </div>
          <p className="mt-1 text-sm text-[#868E95]">
            Maximum file size: 3MB <span className="mx-2">•</span> Supported file formats: PNG, JPEG, JPG, PDF
          </p>
        </div>
      </section>
    )
});

FilesDropzone.displayName = "FilesDropzone";

export default FilesDropzone;
